import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FCanvasComponent, FZoomDirective } from '@foblex/flow';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CampaignService } from 'src/app/private/services/campaign.service';
import { ContactGrouopService } from 'src/app/private/services/contact-groups.service';
import { EmailTemplateService } from 'src/app/private/services/email-tempate.service';
import { UploadService } from 'src/app/private/services/upload.service';
import { LeadService } from 'src/app/private/services/lead.service';
import { ManageCampaignService } from 'src/app/private/services/manage-campaign.service';
import { PipeLineService } from 'src/app/private/services/pipeline.service';
import { UsersService } from 'src/app/private/services/users.service';
import { WorkflowService, WorkflowStatus } from 'src/app/private/services/workflow.service';
import { PermissionService } from 'src/app/private/services/permission.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import {
  CONDITION_OPTIONS,
  STEP_DEFS,
  STEP_GROUPS,
  StepDef,
  StepGroup,
  WAIT_UNITS,
  WorkflowEdge,
  WorkflowModule,
  WorkflowNode,
  campaignPending,
  defaultConfig,
  edgeOutputId,
  inputId,
  isTrigger,
  newId,
  outputId,
  outputsOf,
  stepDef,
} from './workflow-catalog';

interface Named {
  id: number;
  name: string;
  status?: string;
  type?: string;
}

interface EmailFile {
  fileName: string;
  url: string;
}

interface TemplateOption {
  id: string;
  name: string;
  attachments?: EmailFile[];
}

/**
 * Visual builder for one workflow: palette, canvas and the settings of the selected step.
 * Saving stores a draft. Activating asks part-g to check the graph and reports each problem
 * on the step it belongs to.
 */
@Component({
  selector: 'app-workflow-builder',
  templateUrl: './workflow-builder.component.html',
  styleUrls: ['./workflow-builder.component.scss'],
})
export class WorkflowBuilderComponent implements OnInit, OnDestroy {
  readonly groups = STEP_GROUPS;
  readonly steps = STEP_DEFS;
  readonly conditions = CONDITION_OPTIONS;
  readonly waitUnits = WAIT_UNITS;
  readonly campaignPending = campaignPending;

  access = { create: false, update: false, delete: false };
  loading = true;
  loadFailed = false;
  saving = false;
  acting = false;

  workflowId = 0;
  module: WorkflowModule = 'campaign';
  name = '';
  description = '';
  status: WorkflowStatus = 'draft';
  version = 1;

  nodes: WorkflowNode[] = [];
  edges: WorkflowEdge[] = [];
  selectedId: string | null = null;
  tab: 'builder' | 'settings' | 'history' | 'analytics' = 'builder';
  paletteQuery = '';
  zoomPercent = 100;
  historyRuns: any[] = [];
  historyLoading = false;
  historyError = '';
  analyticsLoading = false;
  analyticsError = '';
  analyticsCapped = false;
  analyticsTotals = { entered: 0, active: 0, waiting: 0, finished: 0, stopped: 0, failed: 0 };
  analyticsSteps: {
    id: string;
    label: string;
    detail: string;
    reached: number;
    here: number;
    channel: '' | 'email' | 'whatsapp';
    campaignId: number | string | null;
    sent: number | null;
    delivered: number | null;
    opened: number | null;
    statsReady: boolean;
  }[] = [];

  /** Lead defaults, used when a lead step has to create a lead. */
  leadDefaults: any = {};

  emailCampaigns: Named[] = [];
  whatsappCampaigns: Named[] = [];
  emailTemplates: TemplateOption[] = [];
  whatsappTemplates: TemplateOption[] = [];
  creatingTemplate = false;
  /** File names still uploading, keyed by the step they belong to. */
  pendingUploads: Record<string, string[]> = {};
  groupsList: Named[] = [];
  leadSpaces: Named[] = [];
  owners: Named[] = [];
  pipelines: { id: number; name: string; stages: string[] }[] = [];

  /** Messages from the last activation attempt, keyed by step id. */
  nodeErrors: Record<string, string[]> = {};
  nodeWarnings: Record<string, string[]> = {};
  formErrors: string[] = [];

  /** Rubber-band line, in canvas-wrap pixels, while a port is being connected. */
  draft: { outputId: string; x1: number; y1: number; x2: number; y2: number; moved: boolean; armed: boolean } | null = null;
  hoverInputId: string | null = null;
  /** Clickable labels sitting on saved lines. */
  wireButtons: { id: string; edge: WorkflowEdge; x: number; y: number; text: string }[] = [];
  wrapBox = { w: 1, h: 1 };

  @ViewChild('canvasWrap') canvasWrap?: ElementRef<HTMLElement>;
  @ViewChild(FCanvasComponent) flowCanvas?: FCanvasComponent;
  @ViewChild(FZoomDirective) flowZoom?: FZoomDirective;
  private wireFrame = 0;
  private wireUnlisten: Array<() => void> = [];
  private wireSvg: SVGSVGElement | null = null;
  private readonly svgNs = 'http://www.w3.org/2000/svg';

  private savedSnapshot = '';
  private droppedAt = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private workflows: WorkflowService,
    private campaignTracking: CampaignService,
    private campaigns: ManageCampaignService,
    private emailTemplatesApi: EmailTemplateService,
    private uploads: UploadService,
    private contactGroups: ContactGrouopService,
    private leads: LeadService,
    private pipelinesApi: PipeLineService,
    private users: UsersService,
    private toast: ToastrService,
    private ls: LocalStorageService,
    private permissions: PermissionService,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.permissions.refresh();
    this.access = this.permissions.getAccessObject('workflow_automation');
    this.workflowId = Number(this.route.snapshot.paramMap.get('id'));
    this.load();
    this.loadLookups();
  }

  ngOnDestroy(): void {
    this.unbindWireDrag();
    if (this.wireFrame) cancelAnimationFrame(this.wireFrame);
  }

  get locked(): boolean {
    return this.status === 'active' || this.status === 'archived' || !this.access.update;
  }

  get dirty(): boolean {
    return this.snapshot() !== this.savedSnapshot;
  }

  get selected(): WorkflowNode | null {
    return this.nodes.find((n) => n.id === this.selectedId) || null;
  }

  get settingsHeading(): string {
    switch (this.module) {
      case 'lead':
        return 'Lead details';
      case 'deal':
        return 'Deal details';
      case 'contact':
        return 'Contact details';
      default:
        return 'Campaign Details';
    }
  }

  get settingsAsideTitle(): string {
    switch (this.module) {
      case 'lead':
        return 'Set up your lead details';
      case 'deal':
        return 'Set up your deal details';
      case 'contact':
        return 'Set up your contact details';
      default:
        return 'Set up your campaign details';
    }
  }

  get hasTrigger(): boolean {
    return this.nodes.some((n) => isTrigger(n.type));
  }

  stepsIn(group: StepGroup): typeof STEP_DEFS {
    const query = this.paletteQuery.trim().toLowerCase();
    return this.steps.filter(
      (s) =>
        s.group === group &&
        s.palette !== false &&
        (!s.modules || s.modules.includes(this.module)) &&
        (!query || s.label.toLowerCase().includes(query) || s.hint.toLowerCase().includes(query)),
    );
  }

  paletteLabel(step: StepDef): string {
    const labels: Record<string, string> = {
      trigger_campaign: 'Campaign Trigger',
      send_email_message: 'Send Email',
      send_whatsapp_message: 'Send WhatsApp',
      add_to_group: 'Add to Group',
      update_lead: 'Update Lead',
      create_task: 'Create Task',
      notify_owner: 'Notify Owner',
    };
    return labels[step.type] || step.label;
  }

  groupIcon(group: StepGroup): string {
    switch (group) {
      case 'Triggers':
        return 'fas fa-bolt';
      case 'Actions':
        return 'fas fa-magic';
      case 'Conditions':
        return 'fas fa-code-branch';
      default:
        return 'fas fa-hourglass-half';
    }
  }

  nodeTone(type: string): string {
    if (isTrigger(type)) return 'blue';
    if (type === 'wait') return 'purple';
    if (type === 'if_else' || type === 'multi_condition') return 'amber';
    if (type === 'stop_automation') return 'red';
    if (type === 'end' || type.startsWith('send_')) return 'green';
    return 'blue';
  }

  miniBox(node: WorkflowNode): { left: string; top: string } {
    const xs = this.nodes.map((item) => item.position?.x || 0);
    const ys = this.nodes.map((item) => item.position?.y || 0);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const x = node.position?.x || 0;
    const y = node.position?.y || 0;
    const left = maxX === minX ? 28 : ((x - minX) / (maxX - minX)) * 62 + 12;
    const top = maxY === minY ? 12 : ((y - minY) / (maxY - minY)) * 68 + 8;
    return { left: `${left}%`, top: `${top}%` };
  }

  zoomIn(): void {
    this.flowZoom?.zoomIn();
    this.readZoom();
  }

  zoomOut(): void {
    this.flowZoom?.zoomOut();
    this.readZoom();
  }

  fitCanvas(): void {
    this.flowCanvas?.fitToScreen(undefined, true);
    setTimeout(() => this.readZoom(), 250);
  }

  resetCanvas(): void {
    this.flowZoom?.reset();
    this.zoomPercent = 100;
    this.refreshWires();
  }

  private readZoom(): void {
    const scale = this.flowZoom?.getScale();
    if (scale) this.zoomPercent = Math.round(scale * 100);
    this.refreshWires();
  }

  isStart(type: string): boolean {
    return isTrigger(type);
  }

  def(type: string) {
    return stepDef(type);
  }

  outputs(node: WorkflowNode) {
    return outputsOf(node);
  }

  outId(nodeId: string, key: string): string {
    return outputId(nodeId, key);
  }

  inId(nodeId: string): string {
    return inputId(nodeId);
  }

  connectionOutput(edge: WorkflowEdge): string {
    return edgeOutputId(this.nodes.find((n) => n.id === edge.source), edge);
  }

  connectionInput(edge: WorkflowEdge): string {
    return inputId(edge.target);
  }

  edgeText(edge: WorkflowEdge): string {
    if (!edge.label) return '';
    const node = this.nodes.find((n) => n.id === edge.source);
    return node ? this.outputCaption(node, edge.label) : edge.label;
  }

  nodeSummary(node: WorkflowNode): string {
    const campaign = this.chosenCampaign(node);
    switch (node.type) {
      case 'trigger_campaign':
      case 'trigger_schedule':
      case 'send_email':
      case 'send_whatsapp':
        return campaign?.name || 'Choose a campaign';
      case 'trigger_lead_created':
        return 'When a lead is created';
      case 'trigger_lead_status':
        return node.config.lead_status ? `Status becomes ${node.config.lead_status}` : 'Enter a status';
      case 'trigger_deal_created':
        return 'When a deal is created';
      case 'trigger_deal_stage':
        return node.config.deal_stage ? `Stage becomes ${node.config.deal_stage}` : 'Enter a stage';
      case 'trigger_contact_created':
        return 'When a contact is created';
      case 'trigger_contact_updated':
        return node.config.field && node.config.field !== 'any' ? `When ${this.contactFieldLabel(node.config.field)} changes` : 'When a contact is updated';
      case 'send_email_message':
        if (node.config.mode === 'new') return node.config.template_name || 'New email template';
        return this.templateName(this.emailTemplates, node.config.template_id, node.config.template_name) || 'Choose an email template';
      case 'send_whatsapp_message':
        if (node.config.mode === 'text') return node.config.message ? 'Custom message' : 'Write a message';
        return this.templateName(this.whatsappTemplates, node.config.template_id, node.config.template_name) || 'Choose a template';
      case 'wait':
        return node.config.amount ? `Wait ${node.config.amount} ${node.config.unit || ''}` : 'Set a wait';
      case 'if_else':
        return this.conditions.find((c) => c.value === node.config.condition)?.label || 'Choose a check';
      case 'multi_condition':
        return `${(node.config.branches || []).length} branches`;
      case 'add_to_group':
        return this.groupsList.find((g) => g.id === Number(node.config.contact_group_id))?.name || 'Choose a group';
      case 'update_lead':
        return node.config.lead_status || 'Choose a status';
      case 'create_task':
        return node.config.title || 'Add a task title';
      default:
        return this.def(node.type)?.hint || '';
    }
  }

  toLocalInput(iso: string): string {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  setRunAt(local: string): void {
    this.setConfig('run_at', local ? new Date(local).toISOString() : '');
  }

  outputCaption(node: WorkflowNode, label?: string): string {
    if (!label) return '';
    if (node.type === 'if_else') return label === 'yes' ? 'Yes' : 'No';
    return label;
  }

  openHistory(): void {
    this.tab = 'history';
    this.loadHistory();
  }

  openAnalytics(): void {
    this.tab = 'analytics';
    this.loadAnalytics();
  }

  loadAnalytics(): void {
    this.analyticsLoading = true;
    this.analyticsError = '';
    this.workflows.runs({ client_id: this.ls.getClientId(), workflow_id: this.workflowId, limit: 200 }).subscribe({
      next: (res) => {
        const runs = Array.isArray(res?.data) ? res.data : [];
        this.analyticsCapped = runs.length >= 200;
        this.buildAnalytics(runs);
        this.analyticsLoading = false;
        this.loadCampaignStats();
      },
      error: () => {
        this.analyticsLoading = false;
        this.analyticsError = 'Could not load analytics. Restart the workflow API, then try again.';
      },
    });
  }

  private buildAnalytics(runs: any[]): void {
    const totals = { entered: runs.length, active: 0, waiting: 0, finished: 0, stopped: 0, failed: 0 };
    for (const run of runs) {
      if (run.status === 'completed') totals.finished += 1;
      else if (run.status === 'waiting') totals.waiting += 1;
      else if (run.status === 'stopped') totals.stopped += 1;
      else if (run.status === 'failed') totals.failed += 1;
      else totals.active += 1;
    }
    this.analyticsTotals = totals;
    this.analyticsSteps = this.nodes.map((node) => {
      const reached = runs.filter(
        (run) =>
          String(run.current_node_id) === node.id ||
          (run.steps || []).some((step: any) => String(step.node_id) === node.id),
      ).length;
      const here = runs.filter(
        (run) => String(run.current_node_id) === node.id && run.status !== 'completed' && run.status !== 'stopped',
      ).length;
      const channel = node.type === 'send_email' ? 'email' : node.type === 'send_whatsapp' ? 'whatsapp' : '';
      return {
        id: node.id,
        label: this.def(node.type)?.label || node.type,
        detail: this.nodeSummary(node),
        reached,
        here,
        channel,
        campaignId: channel ? node.config.campaign_id || null : null,
        sent: null,
        delivered: null,
        opened: null,
        statsReady: !channel || !node.config.campaign_id,
      };
    });
  }

  private loadCampaignStats(): void {
    const seen = new Set<string>();
    for (const step of this.analyticsSteps) {
      if (!step.channel || !step.campaignId) continue;
      const key = `${step.channel}:${step.campaignId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const payload = { client_id: this.ls.getClientId(), campaign_id: step.campaignId };
      const request =
        step.channel === 'whatsapp'
          ? this.campaignTracking.getWhatsappCampaignTracking(payload)
          : this.campaignTracking.getCampaignTracking(payload);
      request.subscribe({
        next: (res: any) => this.applyCampaignStats(step.channel, step.campaignId, res?.statistics),
        error: () => this.applyCampaignStats(step.channel, step.campaignId, null),
      });
    }
  }

  private applyCampaignStats(channel: '' | 'email' | 'whatsapp', campaignId: number | string | null, stats: any): void {
    const sent = stats ? Number(stats.sent) || 0 : null;
    const delivered = stats ? Number(stats.delivered) || 0 : null;
    const opened = stats ? Number(channel === 'whatsapp' ? stats.read : stats.opened) || 0 : null;
    this.analyticsSteps = this.analyticsSteps.map((step) =>
      step.channel === channel && String(step.campaignId) === String(campaignId)
        ? { ...step, sent, delivered, opened, statsReady: true }
        : step,
    );
  }

  campaignStat(step: { channel: string; sent: number | null; delivered: number | null; opened: number | null; statsReady: boolean }, key: 'sent' | 'delivered' | 'opened'): string {
    if (!step.channel) return '—';
    if (!step.statsReady) return '…';
    const value = step[key];
    return value == null ? '—' : String(value);
  }

  loadHistory(): void {
    this.historyLoading = true;
    this.historyError = '';
    this.workflows.runs({ client_id: this.ls.getClientId(), workflow_id: this.workflowId }).subscribe({
      next: (res) => {
        const rows = res?.data;
        this.historyRuns = Array.isArray(rows) ? rows : [];
        this.historyLoading = false;
      },
      error: () => {
        this.historyLoading = false;
        this.historyError = 'Could not load execution history. Restart the workflow API, then try again.';
      },
    });
  }

  runStatus(run: any): string {
    if (run.status === 'waiting' && run.waiting_for === 'send') return 'Sending';
    if (run.status === 'waiting') return 'Waiting';
    if (run.status === 'active') return 'In progress';
    if (run.status === 'completed') return 'Finished';
    if (run.status === 'stopped') return 'Stopped';
    if (run.status === 'failed') return 'Failed';
    return run.status || '';
  }

  runStep(run: any): string {
    const node = this.nodes.find((n) => n.id === run.current_node_id);
    if (node) return this.def(node.type)?.label || node.type;
    const last = Array.isArray(run.steps) ? run.steps[run.steps.length - 1] : null;
    return last ? this.def(last.node_type)?.label || last.node_type : '—';
  }

  runPath(run: any): string {
    const steps = Array.isArray(run.steps) ? run.steps : [];
    if (!steps.length) return '';
    return steps.map((s: any) => this.def(s.node_type)?.label || s.node_type).join(' → ');
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  load(): void {
    this.loading = true;
    this.loadFailed = false;
    this.workflows.get({ client_id: this.ls.getClientId(), workflow_id: this.workflowId }).subscribe({
      next: (res) => {
        const w = res?.data;
        if (!w) {
          this.loadFailed = true;
          this.loading = false;
          return;
        }
        this.name = w.name || '';
        this.description = w.description || '';
        this.status = w.status;
        this.version = w.version;
        this.nodes = this.normaliseNodes(w.graph?.nodes);
        this.edges = this.normaliseEdges(w.graph?.edges);
        const storedModule = w.settings?.module;
        this.module = storedModule === 'lead' || storedModule === 'deal' || storedModule === 'contact' ? storedModule : 'campaign';
        this.leadDefaults = { ...(w.settings?.lead_defaults || {}) };
        this.savedSnapshot = this.snapshot();
        this.loading = false;
        this.refreshWires();
      },
      error: () => {
        this.loading = false;
        this.loadFailed = true;
      },
    });
  }

  private loadLookups(): void {
    const clientId = this.ls.getClientId();
    const seeAll = this.ls.is_admin() || this.ls.is_root();
    const where = (type: string) => ({
      client_id: clientId,
      campaign_type: type,
      ...(seeAll ? {} : { user_id: this.ls.getUserId() }),
    });
    const order = { col: '"campaign"."createdAt"', dir: 'DESC' };

    this.campaigns.getAllManangeCampaign({ where: where('email'), page: 0, limit: 200, order }).subscribe({
      next: (res) => (this.emailCampaigns = this.asCampaigns(res)),
      error: () => (this.emailCampaigns = []),
    });
    this.campaigns.getAllManangeCampaign({ where: where('whatsapp'), page: 0, limit: 200, order }).subscribe({
      next: (res) => (this.whatsappCampaigns = this.asCampaigns(res)),
      error: () => (this.whatsappCampaigns = []),
    });
    this.loadMessageTemplates(clientId, seeAll);
    this.contactGroups
      .fetchContactGroupsForSelect({ clientId, userId: this.ls.getUserId(), isAdmin: seeAll })
      .subscribe({
        next: (rows) =>
          (this.groupsList = (rows || []).map((g: any) => ({
            id: Number(g.id),
            name: g.name || g.contact_group_name || `Group ${g.id}`,
          }))),
        error: () => (this.groupsList = []),
      });
    this.leads.getAllLeadSpace({ client_id: clientId }).subscribe({
      next: (res) => {
        const rows = Array.isArray(res) ? res : res?.data || res?.rows || [];
        this.leadSpaces = rows.map((s: any) => ({ id: Number(s.id), name: s.space_name || `Space ${s.id}` }));
      },
      error: () => (this.leadSpaces = []),
    });
    this.users.findAllUsers({ client_id: clientId }).subscribe({
      next: (res) => {
        const rows = Array.isArray(res) ? res : res?.data || res?.rows || [];
        this.owners = rows.map((u: any) => ({
          id: Number(u.user_id ?? u.id),
          name: [u.fname, u.lname].filter(Boolean).join(' ') || u.email || `User ${u.user_id}`,
        }));
      },
      error: () => (this.owners = []),
    });
    this.pipelinesApi.getAllPipeLine({ client_id: clientId, pipeline_type: 'lead' }).subscribe({
      next: (res) => {
        const rows = Array.isArray(res) ? res : res?.data || res?.rows || [];
        this.pipelines = rows.map((p: any) => ({
          id: Number(p.pipeline_id ?? p.id),
          name: p.pipeline_name || p.name || `Pipeline ${p.pipeline_id}`,
          stages: (p.stages || []).map((s: any) => s.stage_name).filter(Boolean),
        }));
      },
      error: () => (this.pipelines = []),
    });
  }

  private loadMessageTemplates(clientId: any, seeAll: boolean): void {
    const scope = seeAll ? { client_id: clientId } : { client_id: clientId, user_id: this.ls.getUserId() };
    this.campaigns.getAllEmailTemplates(scope).subscribe({
      next: (res) => {
        const rows = Array.isArray(res) ? res : res?.rows || res?.data || [];
        this.emailTemplates = (Array.isArray(rows) ? rows : [])
          .map((t: any) => ({
            id: String(t.template_id ?? t.id ?? ''),
            name: t.template_name || 'Template',
            attachments: this.asEmailFiles(t.attachments),
          }))
          .filter((t: TemplateOption) => t.id);
      },
      error: () => (this.emailTemplates = []),
    });
    this.campaigns.getAllTemplatesWithStatusFilter({ ...scope, status: 'APPROVED' }).subscribe({
      next: (res) => {
        const rows = Array.isArray(res) ? res : res?.rows || res?.data || [];
        this.whatsappTemplates = (Array.isArray(rows) ? rows : [])
          .filter((t: any) => String(t.status || t.template_data?.status || 'APPROVED').toUpperCase() === 'APPROVED')
          .map((t: any) => ({
            id: String(t.meta_template_id || t.template_data?.id || ''),
            name: t.template_name || t.elementName || t.template_data?.elementName || 'Template',
          }))
          .filter((t: TemplateOption) => t.id);
      },
      error: () => (this.whatsappTemplates = []),
    });
  }

  private templateName(list: TemplateOption[], id: any, fallback?: string): string {
    const found = list.find((t) => t.id === String(id));
    return found?.name || fallback || '';
  }

  setCampaignChannel(channel: 'email' | 'whatsapp'): void {
    const node = this.selected;
    if (!node || this.locked) return;
    const type = channel === 'whatsapp' ? 'send_whatsapp' : 'send_email';
    if (node.type === type) return;
    const list = channel === 'whatsapp' ? this.whatsappCampaigns : this.emailCampaigns;
    const keep = this.pendingCampaigns(list, node).some((c) => c.id === Number(node.config.campaign_id));
    node.type = type;
    node.config = { ...node.config, campaign_id: keep ? node.config.campaign_id : '' };
    this.clearStepFeedback(node.id);
  }

  setMessageMode(mode: 'template' | 'new' | 'text'): void {
    const node = this.selected;
    if (!node || this.locked) return;
    if (node.type === 'send_email_message' && mode !== 'new') {
      node.config = { ...node.config, mode, attachments: [] };
      this.clearStepFeedback(node.id);
      return;
    }
    this.setConfig('mode', mode);
  }

  selectMessageTemplate(list: TemplateOption[], id: string): void {
    const chosen = list.find((t) => t.id === String(id));
    const node = this.selected;
    if (!node || this.locked) return;
    node.config = {
      ...node.config,
      template_id: id,
      template_name: chosen?.name || node.config.template_name || '',
    };
    this.clearStepFeedback(node.id);
  }

  emailFiles(node: WorkflowNode): EmailFile[] {
    return this.asEmailFiles(node.config.attachments);
  }

  pendingFileNames(node: WorkflowNode): string[] {
    return this.pendingUploads[node.id] || [];
  }

  addEmailAttachments(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';
    const node = this.selected;
    if (!node || this.locked || node.type !== 'send_email_message' || node.config.mode !== 'new') return;

    const room = 5 - this.emailFiles(node).length - this.pendingFileNames(node).length;
    if (room <= 0) {
      this.toast.warning('An email can have up to 5 attachments');
      return;
    }
    if (files.length > room) this.toast.warning('An email can have up to 5 attachments');

    files.slice(0, room).forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        this.toast.error(`"${file.name}" is larger than 10MB`);
        return;
      }
      this.pendingUploads = {
        ...this.pendingUploads,
        [node.id]: [...(this.pendingUploads[node.id] || []), file.name],
      };
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folders', `crm/attachments/${this.ls.getClientId()}`);
      formData.append('bucket', 'global-upload-storage');
      formData.append('client_id', String(this.ls.getClientId()));
      this.uploads.uploadDocumentsWithProgress(formData).subscribe({
        next: (upload: any) => {
          if (upload?.type !== 'response') return;
          this.finishUpload(node.id, file.name);
          const url = upload.body?.url || upload.body?.data?.url;
          if (!url) {
            this.toast.error(`Could not attach ${file.name}`);
            return;
          }
          this.appendEmailFile(node.id, { fileName: file.name, url });
        },
        error: () => {
          this.finishUpload(node.id, file.name);
          this.toast.error(`Could not attach ${file.name}`);
        },
      });
    });
  }

  removeEmailAttachment(index: number): void {
    const node = this.selected;
    if (!node || this.locked) return;
    node.config = { ...node.config, attachments: this.emailFiles(node).filter((_, i) => i !== index) };
    this.clearStepFeedback(node.id);
  }

  private appendEmailFile(nodeId: string, file: EmailFile): void {
    const node = this.nodes.find((n) => n.id === nodeId);
    if (!node) return;
    node.config = { ...node.config, attachments: [...this.emailFiles(node), file] };
    this.clearStepFeedback(nodeId);
  }

  private finishUpload(nodeId: string, fileName: string): void {
    const names = (this.pendingUploads[nodeId] || []).filter((name) => name !== fileName);
    this.pendingUploads = { ...this.pendingUploads, [nodeId]: names };
  }

  private asEmailFiles(raw: any): EmailFile[] {
    if (!Array.isArray(raw)) return [];
    return raw
      .map((file: any) => ({
        fileName: String(file?.fileName || file?.file_name || file?.name || 'File'),
        url: String(file?.url || file?.publicUrl || ''),
      }))
      .filter((file: EmailFile) => file.url);
  }

  createEmailTemplate(): void {
    const node = this.selected;
    if (!node || this.locked || this.creatingTemplate) return;
    const name = String(node.config.template_name || '').trim();
    const subject = String(node.config.subject || '').trim();
    const body = String(node.config.body || '').trim();
    if (!name || !subject || !body) {
      this.toast.warning('Enter a template name, subject, and message');
      return;
    }
    if (this.pendingFileNames(node).length) {
      this.toast.warning('Wait for the attachments to finish uploading');
      return;
    }
    const attachments = this.emailFiles(node);
    this.creatingTemplate = true;
    this.emailTemplatesApi
      .createTemplate({
        client_id: this.ls.getClientId(),
        user_id: this.ls.getUserId(),
        template_name: name,
        template_body: body.replace(/\n/g, '<br>'),
        template_category: 'marketing',
        status: 'active',
        attachments,
        variables: [],
        response_type: 'interested_not_interested',
      })
      .subscribe({
        next: (res: any) => {
          const row = res?.data && !res.template_id ? res.data : res;
          const id = row?.template_id;
          this.creatingTemplate = false;
          if (!id) {
            this.toast.error('The template was not saved');
            return;
          }
          this.emailTemplates = [
            { id: String(id), name, attachments },
            ...this.emailTemplates.filter((t) => t.id !== String(id)),
          ];
          node.config = { ...node.config, mode: 'template', template_id: id, template_name: name, subject, body, attachments };
          this.toast.success('Email template saved');
          this.clearStepFeedback(node.id);
        },
        error: (err: any) => {
          this.creatingTemplate = false;
          const message = err?.error?.message || err?.error || err?.message;
          this.toast.error(typeof message === 'string' && message ? message : 'Could not save the template');
        },
      });
  }

  private asCampaigns(res: any): Named[] {
    const rows = res?.rows || res?.data?.rows || res?.data || (Array.isArray(res) ? res : []);
    return (Array.isArray(rows) ? rows : []).map((c: any) => ({
      id: Number(c.id),
      name: c.campaign_name || `Campaign ${c.id}`,
      status: c.status,
      type: c.campaign_type,
    }));
  }

  private normaliseNodes(raw: any): WorkflowNode[] {
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((n) => n && n.id && stepDef(n.type))
      .map((n, i) => ({
        id: String(n.id),
        type: n.type,
        config: n.config && typeof n.config === 'object' ? { ...n.config } : {},
        position: n.position && Number.isFinite(n.position.x) ? n.position : { x: 280, y: 40 + i * 150 },
      }));
  }

  private normaliseEdges(raw: any): WorkflowEdge[] {
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((e) => e && e.source && e.target)
      .map((e) => ({
        id: String(e.id || newId('e')),
        source: String(e.source),
        target: String(e.target),
        targetHandle: 'in',
        ...(e.sourceHandle ? { sourceHandle: String(e.sourceHandle) } : {}),
        ...(e.label ? { label: String(e.label) } : {}),
      }));
  }

  // ── Canvas ────────────────────────────────────────────────────────────────
  paletteDisabled(type: string): boolean {
    return this.locked || (isTrigger(type) && this.hasTrigger);
  }

  onDropNode(event: { rect?: { x: number; y: number }; data?: { type?: string } }): void {
    const type = event?.data?.type;
    if (!type || this.paletteDisabled(type)) return;
    this.droppedAt = Date.now();
    const position = event.rect ? { x: Math.round(event.rect.x), y: Math.round(event.rect.y) } : undefined;
    this.addNode(type, position);
  }

  onPaletteClick(type: string): void {
    if (Date.now() - this.droppedAt < 500 || this.paletteDisabled(type)) return;
    this.addNode(type);
  }

  private addNode(type: string, position?: { x: number; y: number }): void {
    const node: WorkflowNode = {
      id: newId('n'),
      type,
      config: defaultConfig(type),
      position: position || { x: 280, y: 40 + this.nodes.length * 150 },
    };
    this.nodes = [...this.nodes, node];
    this.selectedId = node.id;
    this.clearStepFeedback(node.id);
    this.refreshWires();
  }

  select(node: WorkflowNode): void {
    this.selectedId = node.id;
  }

  moveNode(node: WorkflowNode, position: { x: number; y: number }): void {
    if (this.locked) return;
    node.position = { x: Math.round(position.x), y: Math.round(position.y) };
    this.refreshWires();
  }

  /** Keep the canvas from stealing the press that should start a connection. */
  blockCanvasDrag(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
  }

  /** Empty canvas cancels a click-to-connect. The top of a step finishes it. */
  onCanvasBackground(event: PointerEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('[data-wf-output], [data-wf-input], .wf-edge-remove')) return;
    if (!this.draft?.armed) return;
    const nodeEl = target?.closest('[data-wf-node]') as HTMLElement | null;
    const nodeId = nodeEl?.getAttribute('data-wf-node');
    if (nodeId && nodeEl && this.targetHint(nodeId) && event.clientY <= nodeEl.getBoundingClientRect().top + 48) {
      const outputId = this.draft.outputId;
      this.draft = null;
      this.commitConnection(outputId, nodeId);
      return;
    }
    this.draft = null;
    this.hoverInputId = null;
    this.paintWires();
  }

  startWire(event: PointerEvent, node: WorkflowNode, out: { key: string; label?: string }): void {
    if (this.locked) return;
    event.stopPropagation();
    event.preventDefault();
    const start = this.portInWrap(`[data-wf-output="${this.outId(node.id, out.key)}"]`, 'out')
      || this.clientToWrap(event.clientX, event.clientY);
    this.draft = { outputId: this.outId(node.id, out.key), x1: start.x, y1: start.y, x2: start.x, y2: start.y, moved: false, armed: false };
    this.hoverInputId = null;
    this.select(node);
    this.bindWireDrag();
    this.paintWires();
  }

  /** Click the top dot to finish a connection started by clicking an output. */
  onInputDown(event: PointerEvent, node: WorkflowNode): void {
    if (!this.draft?.armed || this.locked) return;
    event.preventDefault();
    event.stopPropagation();
    const outputId = this.draft.outputId;
    this.draft = null;
    this.hoverInputId = null;
    this.commitConnection(outputId, node.id);
  }

  draftPath(): string {
    if (!this.draft?.moved) return '';
    return this.curve(this.draft.x1, this.draft.y1, this.draft.x2, this.draft.y2);
  }

  /** Glow every input that can accept the connection being dragged. */
  targetHint(nodeId: string): boolean {
    if (!this.draft) return false;
    const from = this.parseOutput(this.draft.outputId);
    if (!from || from.nodeId === nodeId) return false;
    const node = this.nodes.find((n) => n.id === nodeId);
    if (!node || isTrigger(node.type)) return false;
    return !this.wouldCycle(from.nodeId, nodeId);
  }

  refreshWires(): void {
    if (this.wireFrame) cancelAnimationFrame(this.wireFrame);
    this.wireFrame = requestAnimationFrame(() => {
      this.wireFrame = 0;
      this.paintWires();
    });
  }

  private bindWireDrag(): void {
    this.unbindWireDrag();
    const move = (event: PointerEvent) => {
      if (!this.draft || this.draft.armed) return;
      const point = this.clientToWrap(event.clientX, event.clientY);
      const moved = this.draft.moved || Math.hypot(point.x - this.draft.x1, point.y - this.draft.y1) > 4;
      this.zone.run(() => {
        if (!this.draft) return;
        this.draft = { ...this.draft, x2: point.x, y2: point.y, moved };
        this.hoverInputId = this.inputUnderPointer(event.clientX, event.clientY);
      });
    };
    const up = (event: PointerEvent) => {
      const draft = this.draft;
      const target = this.inputUnderPointer(event.clientX, event.clientY);
      this.unbindWireDrag();
      this.zone.run(() => {
        if (!draft) return;
        if (draft.moved) {
          this.draft = null;
          this.hoverInputId = null;
          if (target) this.commitConnection(draft.outputId, target);
          else this.paintWires();
        } else {
          this.draft = { ...draft, armed: true };
          this.hoverInputId = null;
          this.paintWires();
        }
      });
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    this.wireUnlisten = [
      () => document.removeEventListener('pointermove', move),
      () => document.removeEventListener('pointerup', up),
    ];
  }

  private unbindWireDrag(): void {
    this.wireUnlisten.forEach((fn) => fn());
    this.wireUnlisten = [];
  }

  private commitConnection(outputId: string, targetId: string): void {
    const from = this.parseOutput(outputId);
    const target = this.nodes.find((n) => n.id === targetId);
    if (!from || !target) return;
    if (from.nodeId === target.id) {
      this.toast.error('A step cannot connect to itself');
      this.paintWires();
      return;
    }
    if (isTrigger(target.type)) {
      this.toast.error('Nothing can connect into the trigger');
      this.paintWires();
      return;
    }
    if (this.wouldCycle(from.nodeId, target.id)) {
      this.toast.error('That connection loops back. A workflow cannot loop');
      this.paintWires();
      return;
    }
    const same = this.edges.find((e) => this.sameOutput(e, from));
    if (same && same.target === target.id) {
      this.toast.info('Those steps are already connected');
      this.paintWires();
      return;
    }
    this.edges = [
      ...this.edges.filter((e) => !this.sameOutput(e, from)),
      {
        id: newId('e'),
        source: from.nodeId,
        target: target.id,
        sourceHandle: from.handle,
        targetHandle: 'in',
        ...(from.label ? { label: from.label } : {}),
      },
    ];
    this.toast.success('Connected');
    this.refreshWires();
  }

  /** True when `target` can already reach `source`, so a new line would loop. */
  private wouldCycle(sourceId: string, targetId: string): boolean {
    const seen = new Set<string>();
    const stack = [targetId];
    while (stack.length) {
      const id = stack.pop() as string;
      if (id === sourceId) return true;
      if (seen.has(id)) continue;
      seen.add(id);
      this.edges.forEach((e) => {
        if (e.source === id) stack.push(e.target);
      });
    }
    return false;
  }

  private sameOutput(edge: WorkflowEdge, from: { nodeId: string; handle: string; label?: string }): boolean {
    if (edge.source !== from.nodeId) return false;
    if (edge.sourceHandle) return edge.sourceHandle === from.handle;
    if (from.label) return String(edge.label || '').toLowerCase() === from.label.toLowerCase();
    return !edge.label;
  }

  private paintWires(): void {
    const wrap = this.canvasWrap?.nativeElement;
    if (wrap) this.wrapBox = { w: Math.max(1, wrap.clientWidth), h: Math.max(1, wrap.clientHeight) };
    const svg = this.ensureWireLayer();
    const buttons: { id: string; edge: WorkflowEdge; x: number; y: number; text: string }[] = [];
    if (svg) {
      svg.querySelectorAll('path.wf-wire, text.wf-wire-label').forEach((node) => node.remove());
      this.edges.forEach((edge) => {
        if (this.draft && this.connectionOutput(edge) === this.draft.outputId && this.draft.moved) return;
        const from = this.portInCanvas(`[data-wf-output="${this.connectionOutput(edge)}"]`, 'out');
        const to = this.portInCanvas(`[data-wf-input="${edge.target}"]`, 'in');
        if (!from || !to) return;
        this.addWirePath(svg, this.curve(from.x, from.y, to.x, to.y), false);
        const source = this.nodes.find((n) => n.id === edge.source);
        const text = source && edge.label ? this.outputCaption(source, edge.label) : '';
        if (text) this.addWireLabel(svg, from, to, text);
        const mid = this.canvasToWrap((from.x + to.x) / 2, (from.y + to.y) / 2);
        if (mid) buttons.push({ id: edge.id, edge, x: mid.x, y: mid.y, text });
      });
    }
    this.wireButtons = buttons;
  }

  private ensureWireLayer(): SVGSVGElement | null {
    const canvas = this.canvasWrap?.nativeElement.querySelector('f-canvas') as HTMLElement | null;
    const layers = canvas ? Array.from(canvas.children) : [];
    const host = (layers[1] || layers[0] || null) as HTMLElement | null;
    if (!canvas || !host) return null;
    const width = String(canvas.offsetWidth || 1);
    const height = String(canvas.offsetHeight || 1);
    if (this.wireSvg && this.wireSvg.isConnected) {
      this.wireSvg.setAttribute('width', width);
      this.wireSvg.setAttribute('height', height);
      return this.wireSvg;
    }
    const svg = document.createElementNS(this.svgNs, 'svg');
    svg.setAttribute('class', 'wf-wires');
    svg.setAttribute('width', width);
    svg.setAttribute('height', height);
    svg.style.position = 'absolute';
    svg.style.left = '0';
    svg.style.top = '0';
    svg.style.overflow = 'visible';
    svg.style.pointerEvents = 'none';
    const defs = document.createElementNS(this.svgNs, 'defs');
    const marker = document.createElementNS(this.svgNs, 'marker');
    marker.setAttribute('id', 'wf-arrow');
    marker.setAttribute('viewBox', '0 0 10 10');
    marker.setAttribute('markerWidth', '7');
    marker.setAttribute('markerHeight', '7');
    marker.setAttribute('refX', '8');
    marker.setAttribute('refY', '5');
    marker.setAttribute('orient', 'auto');
    const head = document.createElementNS(this.svgNs, 'path');
    head.setAttribute('d', 'M 0 0 L 10 5 L 0 10 z');
    head.setAttribute('fill', '#c45a00');
    marker.appendChild(head);
    defs.appendChild(marker);
    svg.appendChild(defs);
    host.insertBefore(svg, host.firstChild);
    this.wireSvg = svg;
    return svg;
  }

  private addWirePath(svg: SVGSVGElement, d: string, dashed: boolean): void {
    const path = document.createElementNS(this.svgNs, 'path');
    path.setAttribute('class', 'wf-wire');
    path.setAttribute('d', d);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', '#c45a00');
    path.setAttribute('stroke-width', '2.5');
    path.setAttribute('marker-end', 'url(#wf-arrow)');
    if (dashed) path.setAttribute('stroke-dasharray', '6 4');
    svg.appendChild(path);
  }

  private addWireLabel(svg: SVGSVGElement, from: { x: number; y: number }, to: { x: number; y: number }, text: string): void {
    const label = document.createElementNS(this.svgNs, 'text');
    label.setAttribute('class', 'wf-wire-label');
    label.setAttribute('x', String(from.x + (to.x - from.x) * 0.42));
    label.setAttribute('y', String(from.y + (to.y - from.y) * 0.42 - 6));
    label.setAttribute('fill', '#9a4d00');
    label.setAttribute('font-size', '11');
    label.setAttribute('font-weight', '700');
    label.setAttribute('text-anchor', 'middle');
    label.textContent = text;
    svg.appendChild(label);
  }

  private clientToWrap(clientX: number, clientY: number): { x: number; y: number } {
    const wrap = this.canvasWrap?.nativeElement.getBoundingClientRect();
    if (!wrap) return { x: clientX, y: clientY };
    return { x: clientX - wrap.left, y: clientY - wrap.top };
  }

  private portInWrap(selector: string, side: 'in' | 'out'): { x: number; y: number } | null {
    const el = this.canvasWrap?.nativeElement.querySelector(selector);
    if (!el) return null;
    const box = el.getBoundingClientRect();
    const y = side === 'out' ? box.bottom - 1 : box.top + 1;
    return this.clientToWrap(box.left + box.width / 2, y);
  }

  /** Port position in the canvas's own coordinates, so a line zooms and pans with the steps. */
  private portInCanvas(selector: string, side: 'in' | 'out'): { x: number; y: number } | null {
    const canvas = this.canvasWrap?.nativeElement.querySelector('f-canvas') as HTMLElement | null;
    const el = this.canvasWrap?.nativeElement.querySelector(selector);
    if (!canvas || !el) return null;
    const scale = this.canvasScale(canvas);
    const box = el.getBoundingClientRect();
    const y = side === 'out' ? box.bottom - 1 : box.top + 1;
    return {
      x: (box.left + box.width / 2 - scale.rect.left) / scale.sx,
      y: (y - scale.rect.top) / scale.sy,
    };
  }

  private canvasToWrap(x: number, y: number): { x: number; y: number } | null {
    const canvas = this.canvasWrap?.nativeElement.querySelector('f-canvas') as HTMLElement | null;
    const wrap = this.canvasWrap?.nativeElement;
    if (!canvas || !wrap) return null;
    const scale = this.canvasScale(canvas);
    const wrapRect = wrap.getBoundingClientRect();
    return {
      x: scale.rect.left + x * scale.sx - wrapRect.left,
      y: scale.rect.top + y * scale.sy - wrapRect.top,
    };
  }

  private canvasScale(canvas: HTMLElement): { sx: number; sy: number; rect: DOMRect } {
    const rect = canvas.getBoundingClientRect();
    const sx = rect.width / (canvas.offsetWidth || rect.width || 1);
    const sy = rect.height / (canvas.offsetHeight || rect.height || 1);
    return { sx: sx || 1, sy: sy || 1, rect };
  }

  private inputUnderPointer(clientX: number, clientY: number): string | null {
    const stack = document.elementsFromPoint(clientX, clientY);
    for (const hit of stack) {
      const marked = hit.closest('[data-wf-input]') as HTMLElement | null;
      const markedId = marked?.getAttribute('data-wf-input');
      if (markedId) return markedId;
      const nodeEl = hit.closest('[data-wf-node]') as HTMLElement | null;
      const nodeId = nodeEl?.getAttribute('data-wf-node');
      if (!nodeId || !nodeEl) continue;
      const node = this.nodes.find((n) => n.id === nodeId);
      if (!node || isTrigger(node.type)) return null;
      const top = nodeEl.getBoundingClientRect().top;
      if (clientY <= top + 36) return nodeId;
      return null;
    }
    return null;
  }

  private curve(x1: number, y1: number, x2: number, y2: number): string {
    const drop = Math.max(36, Math.abs(y2 - y1) / 2);
    return `M ${x1} ${y1} C ${x1} ${y1 + drop}, ${x2} ${y2 - drop}, ${x2} ${y2}`;
  }

  removeNode(node: WorkflowNode): void {
    if (this.locked) return;
    this.nodes = this.nodes.filter((n) => n.id !== node.id);
    this.edges = this.edges.filter((e) => e.source !== node.id && e.target !== node.id);
    if (this.selectedId === node.id) this.selectedId = null;
    if (this.draft && this.parseOutput(this.draft.outputId)?.nodeId === node.id) this.draft = null;
    this.refreshWires();
  }

  removeEdge(edge: WorkflowEdge): void {
    if (this.locked) return;
    this.edges = this.edges.filter((e) => e.id !== edge.id);
    this.refreshWires();
  }

  /** `out-<nodeId>-<key>` back to the node, the port, and the branch label. */
  private parseOutput(id: string): { nodeId: string; handle: string; label?: string } | null {
    const match = /^out-(.+)-([^-]+)$/.exec(id);
    if (!match) return null;
    const node = this.nodes.find((n) => n.id === match[1]);
    if (!node) return null;
    const output = outputsOf(node).find((o) => o.key === match[2]);
    return output ? { nodeId: node.id, handle: output.key, label: output.label } : null;
  }

  // ── Step settings ─────────────────────────────────────────────────────────
  setConfig(key: string, value: any): void {
    const node = this.selected;
    if (!node || this.locked) return;
    node.config = { ...node.config, [key]: value };
    this.clearStepFeedback(node.id);
  }

  setNumber(key: string, raw: string | number): void {
    this.setConfig(key, raw === '' || raw === null || raw === undefined ? '' : Number(raw));
  }

  /** Email or WhatsApp, chosen on a trigger before the campaign list is shown. */
  triggerChannel(node: WorkflowNode): 'email' | 'whatsapp' | '' {
    if (node.config.channel === 'email' || node.config.channel === 'whatsapp') return node.config.channel;
    const chosen = [...this.emailCampaigns, ...this.whatsappCampaigns].find(
      (c) => c.id === Number(node.config.campaign_id),
    );
    return chosen?.type === 'whatsapp' || chosen?.type === 'email' ? chosen.type : '';
  }

  campaignsForTrigger(node: WorkflowNode): Named[] {
    const channel = this.triggerChannel(node);
    if (channel === 'whatsapp') return this.pendingCampaigns(this.whatsappCampaigns, node);
    if (channel === 'email') return this.pendingCampaigns(this.emailCampaigns, node);
    return [];
  }

  setTriggerChannel(channel: 'email' | 'whatsapp'): void {
    const node = this.selected;
    if (!node || this.locked) return;
    const list = channel === 'email' ? this.pendingCampaigns(this.emailCampaigns) : this.pendingCampaigns(this.whatsappCampaigns);
    const keep = list.some((c) => c.id === Number(node.config.campaign_id));
    node.config = { ...node.config, channel, campaign_id: keep ? node.config.campaign_id : '' };
    this.clearStepFeedback(node.id);
  }

  campaignsFor(node: WorkflowNode): Named[] {
    if (node.type === 'send_whatsapp') return this.pendingCampaigns(this.whatsappCampaigns, node);
    if (node.type === 'send_email') return this.pendingCampaigns(this.emailCampaigns, node);
    if (node.type === 'trigger_campaign' || node.type === 'trigger_schedule') return this.campaignsForTrigger(node);
    return this.pendingCampaigns([...this.emailCampaigns, ...this.whatsappCampaigns], node);
  }

  /** Pending campaigns only. A campaign already chosen on this step stays visible. */
  private pendingCampaigns(list: Named[], node?: WorkflowNode): Named[] {
    const selectedId = Number(node?.config?.campaign_id);
    return list.filter((c) => this.campaignPending(c.status) || (selectedId && c.id === selectedId));
  }

  chosenCampaign(node: WorkflowNode): Named | undefined {
    return this.campaignsFor(node).find((c) => c.id === Number(node.config.campaign_id));
  }

  setBranch(index: number, field: 'label' | 'condition', value: string): void {
    const node = this.selected;
    if (!node || this.locked || node.type !== 'multi_condition') return;
    const branches = (node.config.branches || []).map((b: any, i: number) =>
      i === index ? { ...b, [field]: value } : b,
    );
    if (field === 'label') this.renameBranchEdges(node.id, node.config.branches[index]?.label, value);
    node.config = { ...node.config, branches };
  }

  addBranch(): void {
    const node = this.selected;
    if (!node || this.locked) return;
    const branches = [...(node.config.branches || []), { label: 'Branch', condition: '' }];
    node.config = { ...node.config, branches };
  }

  removeBranch(index: number): void {
    const node = this.selected;
    if (!node || this.locked) return;
    const branches = (node.config.branches || []).filter((_: any, i: number) => i !== index);
    if (branches.length < 2) return;
    const removed = node.config.branches[index]?.label;
    this.edges = this.edges.filter((e) => !(e.source === node.id && e.label === removed));
    node.config = { ...node.config, branches };
  }

  private renameBranchEdges(nodeId: string, from: string, to: string): void {
    this.edges = this.edges.map((e) => (e.source === nodeId && e.label === from ? { ...e, label: to } : e));
  }

  stageOptions(): string[] {
    const pipeline = this.pipelines.find((p) => p.id === Number(this.leadDefaults.pipeline_id));
    return pipeline?.stages || [];
  }

  setDefault(key: string, raw: string | number): void {
    if (raw === '' || raw === null || raw === undefined) {
      this.leadDefaults = { ...this.leadDefaults, [key]: '' };
      return;
    }
    const numeric = ['lead_space_id', 'user_id', 'pipeline_id'].includes(key);
    this.leadDefaults = { ...this.leadDefaults, [key]: numeric ? Number(raw) : raw };
  }

  // ── Save and activate ─────────────────────────────────────────────────────
  save(then?: () => void): void {
    if (this.locked && this.graphChanged()) {
      this.toast.info('Pause this workflow before changing its steps');
      return;
    }
    this.saving = true;
    const body: any = {
      client_id: this.ls.getClientId(),
      workflow_id: this.workflowId,
      name: this.name.trim(),
      description: this.description,
      settings: { module: this.module, lead_defaults: this.cleanDefaults() },
      updated_by: this.ls.getUserId(),
    };
    if (!this.locked) body.graph = { nodes: this.nodes, edges: this.edges };
    this.workflows.update(body).subscribe({
      next: () => {
        this.saving = false;
        this.savedSnapshot = this.snapshot();
        this.toast.success('Workflow saved');
        if (then) then();
      },
      error: (err) => {
        this.saving = false;
        this.toast.error(err?.error?.message || 'Could not save the workflow');
      },
    });
  }

  activate(): void {
    const go = () => {
      this.acting = true;
      this.workflows
        .setStatus({
          client_id: this.ls.getClientId(),
          workflow_id: this.workflowId,
          status: 'active',
          updated_by: this.ls.getUserId(),
        })
        .subscribe({
          next: (res) => {
            this.acting = false;
            this.status = 'active';
            this.version = res?.data?.version || this.version;
            this.savedSnapshot = this.snapshot();
            this.applyFeedback(res?.errors, res?.warnings);
            const count = res?.warnings?.length || 0;
            this.toast.success(count ? `Workflow activated, with ${count} warning${count > 1 ? 's' : ''}` : 'Workflow activated');
            if (count) this.focusFeedback();
          },
          error: (err) => {
            this.acting = false;
            const body = err?.error || {};
            this.applyFeedback(body.errors, body.warnings);
            this.toast.error(body.message || 'Could not activate the workflow');
            this.focusFeedback();
          },
        });
    };
    if (this.dirty) this.save(go);
    else go();
  }

  pause(): void {
    this.acting = true;
    this.workflows
      .setStatus({
        client_id: this.ls.getClientId(),
        workflow_id: this.workflowId,
        status: 'paused',
        updated_by: this.ls.getUserId(),
      })
      .subscribe({
        next: (res) => {
          this.acting = false;
          this.status = 'paused';
          this.version = res?.data?.version || this.version;
          this.savedSnapshot = this.snapshot();
          this.toast.success('Workflow paused');
        },
        error: (err) => {
          this.acting = false;
          this.toast.error(err?.error?.message || 'Could not pause the workflow');
        },
      });
  }

  private applyFeedback(errors?: { node_id?: string; message: string }[], warnings?: { node_id?: string; message: string }[]): void {
    this.nodeErrors = {};
    this.nodeWarnings = {};
    this.formErrors = [];
    for (const e of errors || []) {
      if (e.node_id) (this.nodeErrors[e.node_id] ||= []).push(e.message);
      else this.formErrors.push(e.message);
    }
    for (const w of warnings || []) {
      if (w.node_id) (this.nodeWarnings[w.node_id] ||= []).push(w.message);
      else this.formErrors.push(w.message);
    }
  }

  private focusFeedback(): void {
    this.tab = 'builder';
    const id = Object.keys(this.nodeErrors)[0] || Object.keys(this.nodeWarnings)[0];
    if (id) this.selectedId = id;
  }

  private clearStepFeedback(id: string): void {
    delete this.nodeErrors[id];
    delete this.nodeWarnings[id];
  }

  private cleanDefaults(): Record<string, any> {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(this.leadDefaults)) {
      if (v !== '' && v !== null && v !== undefined) out[k] = v;
    }
    return out;
  }

  private graphChanged(): boolean {
    const saved = JSON.parse(this.savedSnapshot || '{}');
    return JSON.stringify({ nodes: this.nodes, edges: this.edges }) !== JSON.stringify({ nodes: saved.nodes, edges: saved.edges });
  }

  private snapshot(): string {
    return JSON.stringify({
      name: this.name.trim(),
      description: this.description,
      nodes: this.nodes,
      edges: this.edges,
      leadDefaults: this.cleanDefaults(),
      status: this.status,
    });
  }

  contactFieldLabel(field: string): string {
    if (field === 'email') return 'email';
    if (field === 'phone') return 'phone';
    if (field === 'lead_source') return 'lead source';
    return 'a field';
  }

  back(): void {
    this.router.navigate(['/main/workflow-automation'], { queryParams: { module: this.module } });
  }

  statusLabel(): string {
    return this.status.charAt(0).toUpperCase() + this.status.slice(1);
  }
}
